import type { IZipEntryInfo, ZipBinaryStorageType } from '@monofe/util';
import type { ZipEntry, ZipInfo } from 'unzipit';
import { unzip } from 'unzipit';

function _zipFile(sourceData: ArrayBuffer | Blob): Promise<ZipInfo> {
    return unzip(sourceData);
}

function _isBlob(object: unknown): object is Blob {
    const blobObject = globalThis.Blob as unknown as Blob | undefined;

    if (blobObject === undefined) {
        return false;
    }

    return (
        object instanceof globalThis.Blob ||
        ['[object Blob]', '[object File]'].includes(Object.prototype.toString.call(object))
    );
}

function _isArrayBuffer(object: unknown): object is ArrayBuffer | Buffer {
    const arrayBufferObject = globalThis.ArrayBuffer as unknown as ArrayBuffer | undefined;

    if (arrayBufferObject === undefined) {
        return false;
    }

    return object instanceof globalThis.ArrayBuffer || object instanceof Buffer;
}

async function _unzipEntry<T extends ZipBinaryStorageType>(
    sourceData: T,
    entry: ZipEntry,
): Promise<T> {
    if (_isBlob(sourceData)) {
        const result = await entry.blob();
        return result as T;
    } else if (_isArrayBuffer(sourceData)) {
        const result = await entry.arrayBuffer();
        return result as T;
    } else {
        throw new Error(`unknown zip src-data type ${typeof sourceData}, entry:${entry.name}`);
    }
}

async function _unzipFile<T extends ZipBinaryStorageType>(
    sourceData: T,
    fileName: string,
): Promise<T> {
    const zipFile = await _zipFile(sourceData);

    const matchedEntries = Object.entries(zipFile.entries)
        .filter(entry => entry[1].name === fileName)
        .map(value => value[1]);

    const primaryMatch = matchedEntries.at(0);

    if (!primaryMatch) {
        throw new Error('No matching files found');
    }

    const result = await _unzipEntry<T>(sourceData, primaryMatch);

    return result;
}

async function _unzipAll<T extends ZipBinaryStorageType>(
    sourceData: T,
): Promise<Record<string, T>> {
    const zipFile = await _zipFile(sourceData);

    const _extractEntry = async (entry: Readonly<ZipEntry>): Promise<{ name: string; data: T }> => {
        const data = await _unzipEntry(sourceData, entry);
        return { name: entry.name, data };
    };

    const extractedFiles: { name: string; data: T }[] = await Promise.all(
        Object.entries(zipFile.entries).map(entry => _extractEntry(entry[1])),
    );

    const items: Record<string, T> = extractedFiles.reduce(
        (currentValue, newValue) => Object.assign(currentValue, newValue),
        {},
    );

    return items;
}

async function _listings(blobData: ZipBinaryStorageType): Promise<string[]> {
    const zipFile = await _zipFile(blobData);
    const entries = Object.entries(zipFile.entries).map(entry => entry[1].name);
    return entries;
}

async function _fileInfo<T extends ZipBinaryStorageType>(
    blobData: T,
    fileName: string,
): Promise<IZipEntryInfo> {
    const zipFile = await _zipFile(blobData);
    const entry = Object.entries(zipFile.entries)
        .filter(zipEntry => zipEntry[1].name === fileName)
        .map(zipEntry => zipEntry[1])
        .at(-1);
    if (!entry) {
        throw new Error(`No entry found for file: ${fileName}`);
    }

    return {
        ...entry,
        lastModifiedDate: entry.lastModDate,
    };
}

export default Object.freeze({
    unzipFile: _unzipFile,
    unzipAll: _unzipAll,
    listings: _listings,
    fileInfo: _fileInfo,
});
